import difyService from '@services/dify.service'
import type { FilePayload, FileUploadResponse } from '@typings/agent'
import type { Agent, Conversation, FileRecord, Message } from '@typings/dify'
import { useState, Dispatch, SetStateAction } from 'react'

const useDify = (agents: Agent[], setAgents: Dispatch<SetStateAction<Agent[]>>) => {
  const [user, setUser] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  // Utility to update agents in local storage
  const saveAgentsToStorage = (agents: Agent[]): void => {
    localStorage.setItem('agents', JSON.stringify(agents))
  }

  // New Conversation
  const handleNewConversation = (selectedAgent: Agent): Conversation => {
    const newConversation: Conversation = {
      id: 'temp_' + Date.now().toString(),
      title: 'Nouvelle conversation',
      timestamp: new Date().toISOString(),
      messages: [],
      isFavorite: false,
    }

    setAgents((prevAgents) => {
      const updatedAgents = prevAgents.map((agent) =>
        agent.id === selectedAgent.id
          ? {
              ...agent,
              conversations: [newConversation, ...agent.conversations],
            }
          : agent,
      )
      saveAgentsToStorage(updatedAgents)
      return updatedAgents
    })

    return newConversation
  }

  // Send Message
  const handleSendMessage = async (
    selectedAgent: Agent,
    content: string,
    conversation: Conversation | null,
    responseMode?: 'blocking' | 'streaming',
    setSelectedConversation?: (conversation: Conversation) => void,
    setMessages?: (messages: Message[]) => void,
    setIsAnswering?: (isAnswering: boolean) => void,
    uploadedFile?: FileUploadResponse[],
    onTaskId?: (taskId: string) => void,
  ): Promise<void> => {
    if (!selectedAgent) return

    if (setIsAnswering) setIsAnswering(true)

    // Handle creating a new conversation if one doesn't exist
    if (!conversation) {
      conversation = handleNewConversation(selectedAgent)
      // No need to manually update selectedAgent.conversations here as handleNewConversation updates the state correctly
      if (setSelectedConversation) {
        setSelectedConversation(conversation as Conversation)
      }
    }

    // Add user message to the conversation
    const tempId = 'query_' + Date.now().toString()
    const newMessage: Message = {
      id: tempId,
      content,
      role: 'user',
      timestamp: new Date().toISOString(),
      inputs: {}
    } as Message

    conversation.messages.push(newMessage)
    if (setMessages) setMessages([...conversation.messages])

    //Boucle pour l'envoie de fichier(s)
    try {
      const files: FilePayload[] = []

      // Ajouter les fichiers de la conversation existante
      if (conversation.files && conversation.files.length > 0) {
        files.push(...conversation.files.map((file) => ({
          upload_file_id: file.id,
          type: 'document',
          transfer_method: 'local_file',
          url: file.url || ''
        })))
      }
      // Ajouter les fichiers récemment uploadés
      if (uploadedFile && uploadedFile.length) {
        files.push(...uploadedFile.map((file) => ({
          upload_file_id: file.id,
          type: 'document',
          transfer_method: 'local_file',
          url: ''
        })))
      }
      //Mettre à jour la liste des fichiers de la conversation
      const newFilesConversations: FileRecord[] = []
      if (conversation.files && conversation.files.length > 0) {
        newFilesConversations.push(...conversation.files)
      }
      // Ajouter les nouveaux fichiers uploadés sans écraser les existants
      if (uploadedFile && uploadedFile.length) {
      // Éviter les doublons en vérifiant si le fichier n'existe pas déjà
        const newFiles = uploadedFile.filter(newFile => 
        !newFilesConversations.some(existingFile => existingFile.id === newFile.id)
          ).map((file) => ({
            id: file.id,
            filename: file.name,
            size: file.size,
            extension: file.extension,
            mime_type: file.mime_type,
            url: ''
        }))
  
      newFilesConversations.push(...newFiles)
      }

      // Send message and handle AI response
      const aiResponse = await difyService.sendMessage(
        selectedAgent,
        content,
        conversation.id.startsWith('temp') ? undefined : conversation.id,
        responseMode,
        files,
        (streamedMessage: Message) => {
          // Handle streamed messages
          if (!conversation || !setMessages) return

          const assistantMessage = conversation.messages.find((msg) => msg.id === streamedMessage.id)

          if (assistantMessage) {
            assistantMessage.content = streamedMessage.content
          } else {
            conversation.messages.push(streamedMessage)
          }

          conversation.title = streamedMessage.conversationTitle || conversation.title
          conversation.id = streamedMessage.conversationId || conversation.id
          conversation.files = newFilesConversations

          if (setMessages) setMessages([...conversation.messages])
          if (setSelectedConversation) setSelectedConversation(conversation)
        },
        (error) => {
          setError(`Unable to send the message: ${error}`)
        },
        onTaskId,
      )

      // Update conversation with correct title and ID
      const updatedConversation: Conversation = {
        ...conversation,
        id: aiResponse.conversationId || conversation.id,
        title: aiResponse.conversationTitle || conversation.title || content.slice(0, 50),
        files: newFilesConversations,
      }

      if (setSelectedConversation) {
        setSelectedConversation(updatedConversation)
      }

      // Ajouter la conversation mise à jour à l'agent et mettre à jour les stats de l'agent
      setAgents((prevAgents) => {
        const updatedAgents = prevAgents.map((agent) =>
          agent.id === selectedAgent.id
            ? {
                ...agent,
                conversations: agent.conversations.map((conv) =>
                  conv.id === conversation!.id ? updatedConversation : conv,
                ),
                stats: {
                  ...agent.stats,
                  messageCount: agent.stats.messageCount + 1,
                },
              }
            : agent,
        )
        saveAgentsToStorage(updatedAgents)
        return updatedAgents
      })
    } catch (error) {
      console.error('Error sending message:', error)
      setError(`Unable to send the message: ${error}`)
    } finally {
      if (setIsAnswering) setIsAnswering(false)
    }
  }

  // Delete Conversation
  const handleDeleteConversation = async (selectedAgent: Agent, conversationId: string): Promise<void> => {
    if (!selectedAgent) return

    if (conversationId.startsWith('temp')) {
      // remove the conversation from the agent
      const updatedAgents = agents.map((agent) =>
        agent.id === selectedAgent.id
          ? {
              ...agent,
              conversations: agent.conversations.filter((conv) => conv.id !== conversationId),
            }
          : agent,
      )

      setAgents(updatedAgents)
      saveAgentsToStorage(updatedAgents)
      return
    }

    try {
      await difyService.deleteConversation(selectedAgent, conversationId)

      // update the conversation in the agent and agent stats
      const updatedAgents = agents.map((agent) =>
        agent.id === selectedAgent.id
          ? {
              ...agent,
              stats: {
                ...agent.stats,
                messageCount:
                  (agent.stats?.messageCount || 0) -
                  (agent.conversations
                    .find((conv) => conv.id === conversationId)
                    ?.messages.filter((msg) => msg.role === 'user').length || 0),
                likeCount:
                  (agent.stats?.likeCount || 0) -
                  (agent.conversations.find((conv) => conv.id === conversationId)?.messages.filter((msg) => msg.liked)
                    .length || 0),
              },
              conversations: agent.conversations.filter((conv) => conv.id !== conversationId),
            }
          : agent,
      )

      setAgents(updatedAgents)
      saveAgentsToStorage(updatedAgents)
    } catch (error) {
      console.error('Error deleting conversation:', error)
      setError('Unable to delete the conversation.')
    }
  }

  // Toggle Favorite Conversation
  const handleToggleConversationFavorite = async (selectedAgent: Agent, conversationId: string): Promise<void> => {
    const conversation = selectedAgent.conversations.find((conv) => conv.id === conversationId)
    if (!conversation) return

    const newTitle = conversation.isFavorite
      ? conversation.title.replace('[Favorite] ', '')
      : `[Favorite] ${conversation.title}`

    try {
      await difyService.renameConversation(selectedAgent, conversationId, newTitle)

      const updatedAgents = agents.map((agent) =>
        agent.id === selectedAgent.id
          ? {
              ...agent,
              conversations: agent.conversations.map((conv) =>
                conv.id === conversation.id ? { ...conv, isFavorite: !conversation.isFavorite, title: newTitle } : conv,
              ),
            }
          : agent,
      )

      setAgents(updatedAgents)
      saveAgentsToStorage(updatedAgents)
    } catch (error) {
      console.error('Error toggling favorite status:', error)
      setError('Unable to update favorite status.')
    }
  }

  // Rename Conversation
  const handleRenameConversation = async (
    selectedAgent: Agent,
    conversationId: string,
    newTitle: string,
  ): Promise<void> => {
    try {
      // Call the backend service to rename the conversation
      await difyService.renameConversation(selectedAgent, conversationId, newTitle)

      // Update local state
      const updatedAgents = agents.map((agent) =>
        agent.id === selectedAgent.id
          ? {
              ...agent,
              conversations: agent.conversations.map((conv) =>
                conv.id === conversationId
                  ? {
                      ...conv,
                      title: newTitle.replace('[Favorite] ', ''), // Update the title
                      isFavorite: conv.isFavorite, // Ensure `isFavorite` is preserved
                    }
                  : conv,
              ),
            }
          : agent,
      )

      setAgents(updatedAgents)
      saveAgentsToStorage(updatedAgents)
    } catch (error) {
      console.error('Error renaming conversation:', error)
      setError('Unable to rename the conversation.')
    }
  }

  // like message
  const handleLikeMessage = async (selectedAgent: Agent, conversationId: string, messageId: string): Promise<void> => {
    try {
      // check if message is already liked
      const message = selectedAgent.conversations
        .find((conv) => conv.id === conversationId)
        ?.messages.find((msg) => msg.id === messageId)

      const feedback = !message?.liked ? 'like' : null

      await difyService.sendFeedback(selectedAgent, messageId, feedback)

      // update the message in the conversation and agent stats
      const updatedAgents = agents.map((agent) =>
        agent.id === selectedAgent.id
          ? {
              ...agent,
              stats: {
                ...agent.stats,
                likeCount: (agent.stats?.likeCount || 0) + (feedback === 'like' ? 1 : -1),
              },
              conversations: agent.conversations.map((conv) =>
                conv.id === conversationId
                  ? {
                      ...conv,
                      messages: conv.messages.map((msg) =>
                        msg.id === messageId ? { ...msg, liked: !msg.liked } : msg,
                      ),
                    }
                  : conv,
              ),
            }
          : agent,
      )

      setAgents(updatedAgents)
      saveAgentsToStorage(updatedAgents)
    } catch (error) {
      console.error('Error liking message:', error)
      setError('Unable to like the message.')
    }
  }

  // reorder agents
  const handleReorderAgents = (reorderedAgents: Agent[]) => {
    setAgents(reorderedAgents)
    saveAgentsToStorage(reorderedAgents)
  }

  // favorite agents
  const handleFavoriteAgents = (favoriteAgentId: string) => {
    const favoriteAgentsList: string[] = JSON.parse(localStorage.getItem('favoriteAgents') || '[]')

    const updatedAgents = agents.map((agent) => {
      if (agent.id.toString() === favoriteAgentId) {
        const isAlreadyFavorite = agent.isFavorite

        // Update localStorage based on the favorite state
        if (isAlreadyFavorite) {
          // Remove from favorites
          const updatedList = favoriteAgentsList.filter((id) => id !== favoriteAgentId)
          localStorage.setItem('favoriteAgents', JSON.stringify(updatedList))
        } else {
          // Add to favorites
          favoriteAgentsList.push(favoriteAgentId)
          localStorage.setItem('favoriteAgents', JSON.stringify(favoriteAgentsList))
        }

        // Toggle the agent's favorite status
        return { ...agent, isFavorite: !isAlreadyFavorite }
      }
      return agent
    })

    setAgents(updatedAgents)
    saveAgentsToStorage(updatedAgents)
  }

  // Upload a file for the selected agent
  const handleUploadFile = async (selectedAgent: Agent, file: File): Promise<FileUploadResponse | undefined> => {
    if (!selectedAgent) return undefined

    try {
      const uploadedFile = await difyService.uploadFile(selectedAgent, file)
      return uploadedFile
    } catch (error) {
      console.error('Error uploading file:', error)
      setError('Unable to upload the file.')
    }
  }

  return {
    agents,
    setAgents,
    user,
    setUser,
    error,
    setError,
    sendMessage: handleSendMessage,
    newConversation: handleNewConversation,
    deleteConversation: handleDeleteConversation,
    toggleFavoriteConversation: handleToggleConversationFavorite,
    renameConversation: handleRenameConversation,
    likeMessage: handleLikeMessage,
    reorderAgents: handleReorderAgents,
    favoriteAgents: handleFavoriteAgents,
    uploadFile: handleUploadFile,
  }
}

export default useDify
